import { useState, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockingLoadContext } from '../loading/BlockingLoad';

// `persistAlerts` opts a page out of the auto-dismissal. The 5s default is right for a
// form that answers in milliseconds; it is wrong for a request that takes ten seconds,
// because the user looks away while it runs and comes back to a screen that has already
// erased the only account of what happened. Pages that opt in must give the alert its own
// dismiss control.
export default function useFetch({ setAuth, persistAlerts = false }) {
  const { t } = useTranslation();
  const blocking = useContext(BlockingLoadContext);
  // { [key]: number-in-flight }. A count rather than a flag: two calls sharing a key go
  // 0 -> 1 -> 2 -> 1 -> 0, so the first to settle can no longer clear the second's
  // indicator. That was the bug in the boolean this replaces.
  const [pending, setPending] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (!showAlert || persistAlerts) return;
    const timeout = setTimeout(() => {
      setShowAlert(false);
      setAlertMessage({ message: '', type: '' });
    }, 5000);
    return () => clearTimeout(timeout);
  }, [showAlert, persistAlerts]);

  const bump = useCallback((key, delta) => {
    setPending(prev => {
      const next = Math.max(0, (prev[key] || 0) + delta);
      if (next > 0) return { ...prev, [key]: next };
      const { [key]: settled, ...rest } = prev;
      return rest;
    });
  }, []);

  // isPending() with no key means "any keyed request on this hook". A call that declares
  // neither loadingKey nor blocking is invisible on purpose: that is what a silent
  // background prefetch wants, and anywhere else it is a visible omission.
  const isPending = useCallback(
    key => (key === undefined
      ? Object.keys(pending).length > 0
      : (pending[key] || 0) > 0),
    [pending]
  );

  const fetchResource = useCallback(async ({
    method = 'POST',
    resource = '',
    body = null,
    blocking: isBlocking = false,
    loadingKey = null,
    // For a caller that explains its own failures: no shared alert, same return.
    quiet = false,
  }) => {
    if (isBlocking) blocking.begin();
    if (loadingKey) bump(loadingKey, 1);
    // Kept for the error return: some pages word each refusal by its status.
    let status;

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/dashboard/${resource}`;
      const isFormData = body instanceof FormData;
      const fetchOptions = {
        method,
        mode: 'cors',
        credentials: 'include',
        // FormData sets its own multipart content-type (with boundary)
        ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      };

      if (body) {
        fetchOptions.body = isFormData ? body : JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      status = response.status;
      const result = await response.json();

      if (result.result === 'UNAUTHORIZED') {
        setAuth(prev => ({
          ...prev,
          isLoading: false,
          isLogged: false,
        }));
        throw new Error('UNAUTHORIZED');
      }

      // The body travels with the error: some routes explain a failure in it
      // (the product-fields PUT's landed/failed lists).
      if (result.error) throw Object.assign(new Error(result.error), { body: result });

      // Update auth if valid
      setAuth(prev => ({
        ...prev,
        isLoading: false,
        isLogged: true,
        username: result.username,
        perms: {
          ...prev.perms,
          ...result.perms,
        },
      }));

      return result;
    } catch (err) {
      if (!quiet) {
        setAlertMessage({ message: err.message || t('common.unknownError'), type: 'error' });
        setShowAlert(true);
      }
      return { ...err.body, error: err.message, ...(status !== undefined && { status }) };
    } finally {
      // Both releases live here so a thrown request — including the UNAUTHORIZED
      // logout, which unmounts the tree — gives its count back exactly once. A
      // blocking count left standing means an overlay nothing can take down.
      if (isBlocking) blocking.end();
      if (loadingKey) bump(loadingKey, -1);
    }
  }, [setAuth, t, blocking, bump]);

  return {
    fetchResource,
    isPending,
    showAlert,
    alertMessage,
    setAlertMessage,
    setShowAlert,
  };
}
